import { api } from "@/shared/api";

// FSD features/digital-human — 数字人样片墙(S9-D)。
// 契约锚点:gscq-api DhShowcaseController(租户 /api/v1/digital-human/showcase)+
// DhShowcaseAdminController(平台 /api/v1/platform/dh-showcase)。
// ★红线:recipe 只含平台全局安全资产(avatarId ∈ 预置形象、voiceId ∈ 内置音色),绝不含租户私有声纹/照片。

/** 样片(recipe 四要素 + 展示 label/缩略图/预览)。status/sortWeight 仅平台 admin 视图有值。 */
export interface DhShowcaseDto {
  id: number;
  title: string;
  description?: string | null;
  avatarId: string;
  avatarLabel: string;
  avatarThumbUrl?: string | null;
  voiceId: string;
  voiceLabel: string;
  script: string;
  resolution: string;
  previewUrl?: string | null;
  status?: string | null;
  sortWeight?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** 套用到创作页的 recipe(仅四要素,全 preset 安全资产)。 */
export interface DhShowcaseRecipe {
  avatarId: string;
  voiceId: string;
  script: string;
  resolution: string;
}

/** upsert 入参(平台 admin)。 */
export interface DhShowcaseUpsertRequest {
  title: string;
  description?: string | null;
  avatarId: string;
  voiceId: string;
  script: string;
  resolution: string;
  previewObjectKey?: string | null;
  sortWeight?: number | null;
}

export interface DhShowcasePageResponse<T> {
  records: T[];
  total: number;
  page: number;
  size: number;
}

/** 从样片提取 recipe(创作页预填用)。 */
export function toRecipe(s: DhShowcaseDto): DhShowcaseRecipe {
  return {
    avatarId: s.avatarId,
    voiceId: s.voiceId,
    script: s.script,
    resolution: s.resolution,
  };
}

const TENANT_BASE = "/digital-human/showcase";
const ADMIN_BASE = "/platform/dh-showcase";

export const dhShowcaseService = {
  // ── 租户只读 ──────────────────────────────────────────────
  /** 样片橱窗(仅 PUBLISHED,分页)。 */
  gallery: (params: { page?: number; size?: number } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) qs.set(k, String(v));
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return api.get<DhShowcasePageResponse<DhShowcaseDto>>(`${TENANT_BASE}${suffix}`);
  },

  /** 取单条 recipe(仅 PUBLISHED 可见)。 */
  getForApply: (id: number) => api.get<DhShowcaseDto>(`${TENANT_BASE}/${id}`),

  // ── 平台维护(PLATFORM_ADMIN) ─────────────────────────────
  listAdmin: (params: { status?: string; page?: number; size?: number } = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") qs.set(k, String(v));
    });
    const suffix = qs.toString() ? `?${qs}` : "";
    return api.get<DhShowcasePageResponse<DhShowcaseDto>>(`${ADMIN_BASE}${suffix}`);
  },

  getAdmin: (id: number) => api.get<DhShowcaseDto>(`${ADMIN_BASE}/${id}`),

  create: (req: DhShowcaseUpsertRequest) => api.post<DhShowcaseDto>(`${ADMIN_BASE}`, req),

  update: (id: number, req: DhShowcaseUpsertRequest) =>
    api.put<DhShowcaseDto>(`${ADMIN_BASE}/${id}`, req),

  publish: (id: number) => api.post<DhShowcaseDto>(`${ADMIN_BASE}/${id}/publish`, {}),

  archive: (id: number) => api.post<DhShowcaseDto>(`${ADMIN_BASE}/${id}/archive`, {}),

  toDraft: (id: number) => api.post<DhShowcaseDto>(`${ADMIN_BASE}/${id}/draft`, {}),

  remove: (id: number) => api.delete<void>(`${ADMIN_BASE}/${id}`),

  /** 上传样片预览视频,返 objectKey(随 upsert 提交)+ 即时预览 url。 */
  uploadPreview: (file: File): Promise<{ objectKey: string; previewUrl: string }> => {
    const form = new FormData();
    form.append("file", file);
    return api.post<{ objectKey: string; previewUrl: string }>(
      `${ADMIN_BASE}/uploads/preview`,
      form
    );
  },
};
